import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { CircleAlert, Check, RefreshCw } from 'lucide-react';

import { isoDateFromTimestamp } from '@/portainer/filters/filters';
import { getPlatformType } from '@/react/portainer/environments/utils';
import { PlatformType } from '@/react/portainer/environments/types';
import {
  EnvironmentInfo,
  PolicyEnvironmentType,
  PolicyType,
  isEdgeAsync,
} from '@/react/portainer/policies/types';

import { Badge } from '@@/Badge';
import { Link } from '@@/Link';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { getEnvironmentPolicyStatus } from '../../utils/isEnvironmentTypeSupportedByPolicy';

import { getStatusDisplayLabel } from './statusUtils';

const envColumnHelper = createColumnHelper<EnvironmentInfo>();

export function useEnvironmentColumns(
  policyType?: PolicyType,
  policyEnvType?: PolicyEnvironmentType | 'all'
) {
  return useMemo(
    () => [
      envColumnHelper.accessor('Name', {
        header: 'Environment name',
        id: 'name',
        meta: { width: '250px' },
        enableSorting: true,
        cell: ({ getValue, row: { original: env } }) => {
          const { to: linkTo, params: linkParams } = getEnvironmentLink(env);
          return (
            <Link
              to={linkTo}
              params={linkParams}
              data-cy={`environment-link-${env.Id}`}
              className="truncate"
              title={getValue()}
            >
              {getValue()}
            </Link>
          );
        },
      }),
      envColumnHelper.accessor((env) => getStatusLabel(env, policyType), {
        header: 'Status',
        id: 'status',
        meta: { width: '150px' },
        enableSorting: false,
        cell: ({ getValue }) => {
          const statusLabel = getValue();

          switch (statusLabel) {
            case 'Warning':
            case 'No policy type set':
              return (
                <Badge type="warn">
                  <CircleAlert className="h-3 w-3 mr-1" />
                  {statusLabel}
                </Badge>
              );
            case 'Failed':
              // Show "Retrying" since failed charts will automatically retry on next poll
              return (
                <Badge type="warn">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retrying
                </Badge>
              );
            case 'Not supported':
              return <Badge type="muted">{statusLabel}</Badge>;
            case 'In progress':
              return <Badge type="info">{statusLabel}</Badge>;
            case 'Active':
              return (
                <Badge type="success">
                  <Check className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              );
            default:
              return <Badge type="muted">{statusLabel}</Badge>;
          }
        },
      }),
      envColumnHelper.accessor(
        (row) => {
          if (!policyType) {
            return "Policy doesn't have an type set";
          }
          if (!policyEnvType) {
            return "Policy doesn't have an environment type set";
          }

          const status = getEnvironmentPolicyStatus(row, policyType);

          return status.Message || '-';
        },
        {
          header: 'Message',
          id: 'message',
          meta: { width: 'auto', minWidth: '200px' },
          enableSorting: false,
          cell: ({ getValue }) => {
            const message = getValue();
            const isLongMessage = message.length > 80;

            if (!isLongMessage) {
              return (
                <span className="text-sm text-muted whitespace-normal break-words">
                  {message}
                </span>
              );
            }

            return (
              <TooltipWithChildren message={message} position="top">
                <span className="text-sm text-muted whitespace-normal break-words line-clamp-2 cursor-help">
                  {message}
                </span>
              </TooltipWithChildren>
            );
          },
        }
      ),
      envColumnHelper.accessor(
        (row) => {
          if (!policyType) return undefined;
          const status = getEnvironmentPolicyStatus(row, policyType);
          return status.LastAttemptTime
            ? isoDateFromTimestamp(status.LastAttemptTime)
            : undefined;
        },
        {
          header: 'Last Attempt',
          id: 'lastAttempt',
          meta: { width: '180px' },
          cell: ({ getValue }) => getValue() ?? '-',
        }
      ),
    ],
    [policyType, policyEnvType]
  );
}

export function getStatusLabel(env: EnvironmentInfo, policyType?: PolicyType) {
  if (!policyType) {
    return 'No policy type set';
  }
  const status = getEnvironmentPolicyStatus(env, policyType);

  return getStatusDisplayLabel(status.Status);
}

export function getEnvironmentLink(env: EnvironmentInfo) {
  const platform = getPlatformType(env.Type);
  const isAsync = isEdgeAsync(env);

  if (isAsync) {
    return {
      to: 'edge.browse.dashboard',
      params: { endpointId: env.Id },
    };
  }

  if (platform === PlatformType.Kubernetes) {
    return {
      to: 'kubernetes.dashboard',
      params: { endpointId: env.Id },
    };
  }

  return {
    to: 'docker.dashboard',
    params: { endpointId: env.Id },
  };
}
